import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { postMessageRN, useReactNativeProps, WebToRNAction } from '@eika-infrastruktur/mobilbank-lib';
import { SigningMethodType } from '@eika-infrastruktur/mobilbank-lib/src/nativeProps';
import { IconTypes } from '@eika/icon';
import { ErrorLabel } from '@eika/label';
import { LinkList } from '@eika/link-list';
import { ToggleSwitch } from '@eika/toggle-switch';

import ButtonLinkItem from '../../components/ButtonLinkItem/ButtonLinkItem';
import { Content } from '../../components/shared/Content';
import { useConfig } from '../../defaultConfig';
import { useMultipleAgreements } from '../../fetchHooks/useProfile';
import useSigningMethod from '../../fetchHooks/useSigningMethod';
import useStepUp from '../../hooks/useStepUp';
import useTranslation, { format } from '../../hooks/useTranslation';

import '../Settings/Settings.scss';
import './Login.scss';

type BiometryError = import('@eika-infrastruktur/mobilbank-lib').BiometryError;

const Login: FC = () => {
    const {
        myActivationsLink,
        selectSigningAndLoginMethodLink,
        stepUpAuthLink,
        featureToggles: { enableChooseLoginAndSigningMethod },
    } = useConfig();
    const { bankRelationships, biometrics, loginMethod, signingMethod, supports } = useReactNativeProps();
    const translated = useTranslation();
    const [error, setError] = useState<BiometryError>();
    const [checked, setChecked] = useState(biometrics?.biometricsEnabledApp);
    const { deviceId } = useReactNativeProps();
    const { data: signingMethodQueryResult } = useSigningMethod(deviceId ?? '');

    const openChangeBank = () => {
        postMessageRN({ action: WebToRNAction.OPEN_CHANGE_BANK, payload: undefined });
    };
    const { showStepupConfiguration } = useStepUp();

    const { data: hasMultipleAgreements, isLoading: isLoadingAgreements } = useMultipleAgreements();
    const hasSeveralBanksOrAgreements = !bankRelationships || bankRelationships.length > 1 || hasMultipleAgreements;
    const toggleBiometric = (value: boolean) => {
        setError(undefined);
        if (value) {
            postMessageRN({ action: WebToRNAction.TURN_ON_BIOMETRIC, payload: undefined });
        } else {
            postMessageRN({ action: WebToRNAction.TURN_OFF_BIOMETRIC, payload: undefined });
        }
    };
    useEffect(() => {
        setChecked(biometrics?.biometricsEnabledApp ?? false);
        setError(biometrics?.biometricCapabilities?.error);
    }, [biometrics]);

    const description = useMemo((): string => {
        if (signingMethod?.type && signingMethod.type === loginMethod?.type) {
            return format(
                translated.login.signingAndLoginChoice,
                translated.loginAndSigning.signingMethods[signingMethod.type],
            );
        }
        if (signingMethod?.type && loginMethod?.type) {
            return format(
                translated.login.signingAndLoginChoiceDifferent,
                translated.loginAndSigning.signingMethods[loginMethod.type],
                translated.loginAndSigning.signingMethods[signingMethod.type],
            );
        }
        if (!signingMethod?.type && loginMethod?.type && signingMethodQueryResult?.bankIdSigningMethod) {
            return format(
                translated.login.signingAndLoginChoiceDifferent,
                translated.loginAndSigning.signingMethods[loginMethod.type],
                translated.loginAndSigning.signingMethods[
                    signingMethodQueryResult?.bankIdSigningMethod as SigningMethodType
                ],
            );
        }
        return '';
    }, [
        signingMethod?.type,
        loginMethod?.type,
        signingMethodQueryResult?.bankIdSigningMethod,
        translated.login.signingAndLoginChoice,
        translated.login.signingAndLoginChoiceDifferent,
        translated.loginAndSigning.signingMethods,
    ]);

    const returnHeader = useCallback(() => {
        if (isLoadingAgreements) return <Skeleton width={220} height={22} />;
        if (bankRelationships && bankRelationships.length > 1 && hasMultipleAgreements) {
            return translated.login.changeBankAndAgreement;
        }
        if (hasMultipleAgreements) {
            return translated.login.changeAgreement;
        }
        return translated.login.changeBank;
    }, [
        bankRelationships,
        hasMultipleAgreements,
        isLoadingAgreements,
        translated.login.changeAgreement,
        translated.login.changeBank,
        translated.login.changeBankAndAgreement,
    ]);

    return (
        <>
            <Content>
                <LinkList className="settings__link-list">
                    {selectSigningAndLoginMethodLink?.url &&
                    supports?.loginAndSigningMethod &&
                    enableChooseLoginAndSigningMethod ? (
                        <LinkList.Item href={selectSigningAndLoginMethodLink.url} description={description}>
                            {translated.login.loginAndSigning}
                        </LinkList.Item>
                    ) : (
                        <>
                            {biometrics?.biometricsEnabledOS &&
                                !!biometrics.biometricCapabilities &&
                                biometrics.biometricCapabilities.type !== 'none' && (
                                    <div className="settings__list-item">
                                        <div className="settings__list-item-row">
                                            {format(
                                                translated.login.toggleBiometrics,
                                                translated.biometricType[biometrics.biometricCapabilities.type],
                                            )}
                                            <ToggleSwitch checked={!!checked} onChange={toggleBiometric} />
                                        </div>
                                        <div role="alert">
                                            {error && (
                                                <ErrorLabel
                                                    className="efaktura-notification__email-error"
                                                    icon={IconTypes.UTROPSTEGN_24}
                                                    text={translated.login.biometricsError}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                        </>
                    )}
                    {hasSeveralBanksOrAgreements && (
                        <ButtonLinkItem onClick={openChangeBank}>{returnHeader()}</ButtonLinkItem>
                    )}
                    {myActivationsLink?.url && (
                        <LinkList.Item href={myActivationsLink.url}>{translated.login.myActivations}</LinkList.Item>
                    )}
                    {showStepupConfiguration && !!stepUpAuthLink?.url && (
                        <LinkList.Item href={stepUpAuthLink.url} description={translated.login.stepUp.description}>
                            {translated.login.stepUp.title}
                        </LinkList.Item>
                    )}
                </LinkList>
            </Content>
        </>
    );
};

export default Login;
